/**
 * AriaStandaloneUI - Standalone React component for Aria SDK
 * 
 * This component is mounted by AriaCore.init() and manages its own state
 * independently of any parent React context. It listens to window events
 * dispatched by AriaCore for programmatic control.
 */

import { useState, useEffect, useCallback, useRef, type FC } from 'react';
import { 
  Sparkles, 
  MessageCircle, 
  Mic, 
  MicOff, 
  X, 
  Phone, 
  RefreshCw, 
  Minimize2, 
  Maximize2, 
  Wrench,
  Send,
  User,
  Bot,
} from 'lucide-react';
import type { 
  ChatMessage, 
  TriggerMode, 
  SessionState, 
  SessionManagerStatus,
  SessionManagerEvent,
  ToolCallState,
  Theme,
} from '@/types';
import { AriaSessionManager } from '@/lib/AriaSessionManager';
import { ShadowContainer } from './ShadowContainer';
import { getAriaStyles } from '@/lib/cssLoader';
import { cn } from '@/lib/utils';
import ariaAvatar from '@/assets/aria-avatar.jpg';

interface AriaStandaloneUIProps {
  theme: Theme;
  triggerLabel?: string;
  container: HTMLElement;
}

export const AriaStandaloneUI: FC<AriaStandaloneUIProps> = ({
  theme,
  triggerLabel,
  container,
}) => {
  // UI State
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setTriggerMode] = useState<TriggerMode>('user');
  const [displayMode, setDisplayMode] = useState<'center' | 'bottom-right'>('bottom-right');
  
  // Tool call state
  const [toolCallState, setToolCallState] = useState<ToolCallState>({
    isActive: false,
    toolName: null,
    toolData: null,
  });
  
  // Session State
  const [sessionState, setSessionState] = useState<SessionState>({
    session_id: null,
    token: null,
    expires_at: null,
    isSessionReady: false,
    lastError: null,
    isPreloaded: false,
    isRefreshing: false,
  });
  const [sessionManagerStatus, setSessionManagerStatus] = useState<SessionManagerStatus>('idle');
  const [, setIsSessionPaused] = useState(false);

  // Refs
  const managerRef = useRef<AriaSessionManager | null>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const isOpenRef = useRef(isOpen); // Track isOpen in a ref for event callbacks
  
  // Keep the ref in sync with state
  isOpenRef.current = isOpen;

  // ============================================================================
  // Session Manager Event Handlers
  // ============================================================================

  const handleStatusChange = useCallback((event: SessionManagerEvent) => {
    const { newStatus } = event.data as { oldStatus: SessionManagerStatus; newStatus: SessionManagerStatus };
    setSessionManagerStatus(newStatus);
    
    // Use isOpenRef.current to get the latest value (avoids stale closure issue)
    if (newStatus === 'preloading' || newStatus === 'connecting') {
      if (isOpenRef.current) {
        setIsLoading(true);
      }
    } else if (newStatus === 'ready') {
      setIsLoading(false);
      setIsConnected(true);
    } else if (newStatus === 'error') {
      setIsLoading(false);
    }
  }, []); // Remove isOpen dependency since we use ref now

  const handleSessionReady = useCallback((event: SessionManagerEvent) => {
    const eventData = event.data as { 
      sessionId?: string | null; 
      tokenReady?: boolean; 
      lazyInit?: boolean;
      streamReady?: boolean;
    } | undefined;
    console.log('[AriaStandaloneUI] Session ready event:', eventData, 'isOpen:', isOpenRef.current);
    
    if (managerRef.current) {
      const state = managerRef.current.getSessionState();
      setSessionState(state);
      
      // Check if this is a lazy init event (token ready but no stream yet)
      // vs a full session ready event (stream is available)
      const isLazyInit = eventData?.lazyInit === true;
      const isStreamReady = eventData?.streamReady === true;
      
      if (isStreamReady) {
        // Full session ready with video stream available
        // Attach video stream now that it's available
        if (videoElementRef.current) {
          console.log('[AriaStandaloneUI] Attaching video stream after full session ready (streamReady=true)');
          managerRef.current.attachToVideoElement(videoElementRef.current);
        }
        
        setIsConnected(true);
        setIsLoading(false);
        setError(null);
      } else if (isLazyInit && eventData?.tokenReady) {
        // Lazy init - token is ready
        // If widget is open, initialize the Anam client now
        // Use isOpenRef.current to get the latest value (avoids stale closure issue)
        if (isOpenRef.current) {
          const isAnamReady = managerRef.current.isAnamClientInitialized();
          if (!isAnamReady) {
            console.log('[AriaStandaloneUI] Token received while widget is open, initializing Anam client');
            setIsLoading(true);
            managerRef.current.initializeSessionOnDemand()
              .then(() => {
                console.log('[AriaStandaloneUI] Anam client initialized after token received');
                setIsLoading(false);
                setIsConnected(true);
                setError(null);
                // Attach video stream now that it's ready
                if (videoElementRef.current && managerRef.current) {
                  console.log('[AriaStandaloneUI] Attaching video stream after lazy init');
                  managerRef.current.attachToVideoElement(videoElementRef.current);
                }
              })
              .catch((err) => {
                console.error('[AriaStandaloneUI] Failed to initialize Anam client:', err);
                setIsLoading(false);
                setError(err instanceof Error ? err.message : 'Failed to initialize session');
              });
          }
        } else {
          // Widget not open - just log that token is ready
          console.log('[AriaStandaloneUI] Token ready (lazy init), button enabled');
        }
      } else {
        // Legacy event format without streamReady flag - try to attach anyway
        if (videoElementRef.current) {
          console.log('[AriaStandaloneUI] Attaching video stream (legacy event)');
          managerRef.current.attachToVideoElement(videoElementRef.current);
        }
        setIsConnected(true);
        setIsLoading(false);
        setError(null);
      }
    }
  }, []);

  const handleSessionError = useCallback((event: SessionManagerEvent) => {
    const errorMsg = event.error?.message || 'Session error';
    console.error('[AriaStandaloneUI] Session error:', errorMsg);
    setError(errorMsg);
    setIsLoading(false);
    
    if (managerRef.current) {
      const state = managerRef.current.getSessionState();
      setSessionState(state);
    }
  }, []);

  const handleConnectionChange = useCallback((event: SessionManagerEvent) => {
    const { connected } = event.data as { connected: boolean };
    setIsConnected(connected);
    
    if (!connected && managerRef.current) {
      const state = managerRef.current.getSessionState();
      setSessionState(state);
    }
  }, []);

  const handleMessageHistory = useCallback((event: SessionManagerEvent) => {
    const { messages } = event.data as { messages?: ChatMessage[] };
    if (messages) {
      setChatMessages(messages);
    }
  }, []);

  const handleMessageStream = useCallback((event: SessionManagerEvent) => {
    const { liveTranscript: transcript } = event.data as { liveTranscript?: string };
    if (transcript !== undefined) {
      setLiveTranscript(transcript);
    }
  }, []);

  const handleToolCall = useCallback((event: SessionManagerEvent) => {
    const data = event.data as { isActive: boolean; toolName: string | null; toolData: Record<string, unknown> | null };
    setToolCallState({
      isActive: data.isActive,
      toolName: data.toolName,
      toolData: data.toolData,
    });
  }, []);

  // ============================================================================
  // Video Element Management
  // ============================================================================

  const setVideoElement = useCallback((element: HTMLVideoElement | null) => {
    const previousElement = videoElementRef.current;
    videoElementRef.current = element;
    
    if (element && managerRef.current) {
      // Only attach if video stream is available (session is ready)
      // Otherwise, the stream will be attached when session_ready event fires
      const hasVideoStream = managerRef.current.getVideoStream() !== null;
      if (hasVideoStream) {
        console.log('[AriaStandaloneUI] Attaching video stream to element');
        managerRef.current.attachToVideoElement(element);
      } else {
        console.log('[AriaStandaloneUI] Video element registered, waiting for stream to be ready');
      }
    } else if (!element && previousElement && managerRef.current) {
      console.log('[AriaStandaloneUI] Detaching video stream from element');
      managerRef.current.detachFromVideoElement(previousElement);
    }
  }, []);

  // ============================================================================
  // Assistant Actions
  // ============================================================================

  const openAssistant = useCallback((mode: TriggerMode = 'user') => {
    setTriggerMode(mode);
    setDisplayMode(mode === 'websocket' ? 'center' : 'bottom-right');
    setIsOpen(true);
    setIsMinimized(false);
    setError(null);

    if (!managerRef.current) return;

    const isPaused = managerRef.current.isSessionPaused();
    console.log('[AriaStandaloneUI] openAssistant called, isPaused:', isPaused);
    
    if (isPaused) {
      setIsLoading(true);
      managerRef.current.resumeSession().then((resumed) => {
        setIsSessionPaused(false);
        
        if (resumed) {
          // Session was successfully resumed
          setIsLoading(false);
          setIsConnected(true);
          const state = managerRef.current!.getSessionState();
          setSessionState(state);
          setChatMessages(managerRef.current!.getChatMessages());
          // Re-attach video stream after resume
          if (videoElementRef.current && managerRef.current) {
            console.log('[AriaStandaloneUI] Attaching video stream after resume');
            managerRef.current.attachToVideoElement(videoElementRef.current);
          }
        } else {
          // Session could not be resumed, start new session
          const hasToken = managerRef.current!.hasSessionToken();
          
          if (hasToken) {
            managerRef.current!.initializeSessionOnDemand()
              .then(() => {
                setIsLoading(false);
                setIsConnected(true);
                const state = managerRef.current!.getSessionState();
                setSessionState(state);
                // Attach video stream now that it's ready
                if (videoElementRef.current && managerRef.current) {
                  console.log('[AriaStandaloneUI] Attaching video stream after initialization (from resume)');
                  managerRef.current.attachToVideoElement(videoElementRef.current);
                }
              })
              .catch((initErr) => {
                setIsLoading(false);
                setError(initErr instanceof Error ? initErr.message : 'Failed to start new session');
              });
          } else if (!managerRef.current!.isConnected()) {
            managerRef.current!.connect();
          }
        }
      }).catch((err) => {
        setIsLoading(false);
        setIsSessionPaused(false);
        setError(err instanceof Error ? err.message : 'Failed to resume session');
      });
      return;
    }

    const isAnamReady = managerRef.current.isAnamClientInitialized();
    const hasToken = managerRef.current.hasSessionToken();
    const status = managerRef.current.getStatus();
    
    console.log('[AriaStandaloneUI] openAssistant state check:', { isAnamReady, hasToken, status, isPaused: false });
    
    if (isAnamReady && status === 'ready') {
      console.log('[AriaStandaloneUI] Anam already ready, attaching video');
      setIsLoading(false);
      setIsConnected(true);
      // Re-attach video stream in case element was registered before
      if (videoElementRef.current) {
        managerRef.current.attachToVideoElement(videoElementRef.current);
      }
    } else if (hasToken) {
      console.log('[AriaStandaloneUI] Has token, calling initializeSessionOnDemand');
      setIsLoading(true);
      
      managerRef.current.initializeSessionOnDemand()
        .then(() => {
          setIsLoading(false);
          setIsConnected(true);
          const state = managerRef.current!.getSessionState();
          setSessionState(state);
          // Attach video stream now that it's ready
          if (videoElementRef.current && managerRef.current) {
            console.log('[AriaStandaloneUI] Attaching video stream after initialization');
            managerRef.current.attachToVideoElement(videoElementRef.current);
          }
        })
        .catch((err) => {
          setIsLoading(false);
          setError(err instanceof Error ? err.message : 'Failed to initialize session');
        });
    } else {
      // No token yet - this shouldn't happen since button is disabled until ready
      // But handle it gracefully just in case
      console.warn('[AriaStandaloneUI] No token available, button should have been disabled');
      setError('Session not ready. Please wait and try again.');
    }
  }, []);

  const minimizeAssistant = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizeAssistant = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const closeAssistant = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
    
    if (!managerRef.current) return;
    
    if (videoElementRef.current) {
      managerRef.current.detachFromVideoElement(videoElementRef.current);
    }
    
    // Clear live transcript display (but keep in memory)
    setLiveTranscript('');
    
    // Always pause session when closing widget - session only ends on logout or unmount
    console.log('[AriaStandaloneUI] Pausing session (widget closed)');
    managerRef.current.pauseSession();
    setIsSessionPaused(true);
  }, []);

  const handleWebSocketTrigger = useCallback(() => {
    console.log('[AriaStandaloneUI] WebSocket trigger event');
    openAssistant('websocket');
  }, [openAssistant]);

  // ============================================================================
  // Initialize Session Manager
  // ============================================================================

  useEffect(() => {
    // Get the existing singleton instance (created by AriaCore.init)
    if (!AriaSessionManager.hasInstance()) {
      console.error('[AriaStandaloneUI] AriaSessionManager not initialized');
      return;
    }

    const manager = AriaSessionManager.getInstance({} as never); // Get existing instance
    managerRef.current = manager;

    // Set up event listeners
    manager.addEventListener('status_change', handleStatusChange);
    manager.addEventListener('session_ready', handleSessionReady);
    manager.addEventListener('session_error', handleSessionError);
    manager.addEventListener('connection_change', handleConnectionChange);
    manager.addEventListener('message_history', handleMessageHistory);
    manager.addEventListener('message_stream', handleMessageStream);
    manager.addEventListener('tool_call', handleToolCall);
    manager.setWebSocketTriggerCallback(handleWebSocketTrigger);

    // Sync initial state from manager (deferred to avoid synchronous setState in effect)
    const syncInitialState = () => {
      setSessionState(manager.getSessionState());
      setSessionManagerStatus(manager.getStatus());
      setIsConnected(manager.isConnected());
      setChatMessages(manager.getChatMessages());
      setLiveTranscript(manager.getLiveTranscript());
      setIsMuted(manager.isMuted());
      setIsSessionPaused(manager.isSessionPaused());
      
      const toolState = manager.getToolCallState();
      setToolCallState(toolState);
    };
    
    // Use queueMicrotask to defer state sync and avoid cascading renders warning
    queueMicrotask(syncInitialState);

    return () => {
      manager.removeEventListener('status_change', handleStatusChange);
      manager.removeEventListener('session_ready', handleSessionReady);
      manager.removeEventListener('session_error', handleSessionError);
      manager.removeEventListener('connection_change', handleConnectionChange);
      manager.removeEventListener('message_history', handleMessageHistory);
      manager.removeEventListener('message_stream', handleMessageStream);
      manager.removeEventListener('tool_call', handleToolCall);
      manager.setWebSocketTriggerCallback(null);
    };
  }, [
    handleStatusChange,
    handleSessionReady,
    handleSessionError,
    handleConnectionChange,
    handleMessageHistory,
    handleMessageStream,
    handleToolCall,
    handleWebSocketTrigger,
  ]);

  // ============================================================================
  // Window Event Listeners (for AriaCore programmatic control)
  // ============================================================================

  useEffect(() => {
    const handleOpen = (e: Event) => {
      const customEvent = e as CustomEvent<{ mode?: TriggerMode }>;
      openAssistant(customEvent.detail?.mode || 'user');
    };

    const handleClose = () => {
      closeAssistant();
    };

    const handleMinimize = () => {
      minimizeAssistant();
    };

    const handleMaximize = () => {
      maximizeAssistant();
    };

    window.addEventListener('aria:open', handleOpen);
    window.addEventListener('aria:close', handleClose);
    window.addEventListener('aria:minimize', handleMinimize);
    window.addEventListener('aria:maximize', handleMaximize);

    return () => {
      window.removeEventListener('aria:open', handleOpen);
      window.removeEventListener('aria:close', handleClose);
      window.removeEventListener('aria:minimize', handleMinimize);
      window.removeEventListener('aria:maximize', handleMaximize);
    };
  }, [openAssistant, closeAssistant, minimizeAssistant, maximizeAssistant]);

  // Update container data attribute when open state changes
  useEffect(() => {
    container.setAttribute('data-aria-open', String(isOpen));
  }, [isOpen, container]);

  // ============================================================================
  // Chat Actions
  // ============================================================================

  const toggleChat = useCallback(() => {
    setIsChatVisible(prev => !prev);
  }, []);

  const sendMessage = useCallback(async (message: string) => {
    if (!managerRef.current || !isConnected) {
      throw new Error('Not connected to Aria');
    }

    await managerRef.current.sendMessage(message);
  }, [isConnected]);

  const toggleMute = useCallback(() => {
    if (!managerRef.current) return;
    const newMuteState = managerRef.current.toggleMute();
    setIsMuted(newMuteState);
  }, []);

  const refreshSession = useCallback(() => {
    if (!managerRef.current) return;
    setIsLoading(true);
    setError(null);
    managerRef.current.refreshSession().catch((err) => {
      setError(err instanceof Error ? err.message : 'Failed to refresh session');
      setIsLoading(false);
    });
  }, []);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <>
      {/* Floating Trigger Button - only shown when assistant is closed AND token received */}
      {!isOpen && sessionState.token !== null && (
        <AriaTriggerButtonStandalone 
          showAvatar={true} 
          label={triggerLabel}
          theme={theme}
          isReady={sessionState.isPreloaded || sessionManagerStatus === 'ready' || (sessionManagerStatus === 'connected' && sessionState.token !== null)}
          onClick={() => openAssistant('user')}
        />
      )}

      {/* Assistant Widget - render when open */}
      {isOpen && (
        <>
          {/* Backdrop overlay - only shown when widget is NOT minimized */}
          {!isMinimized && (
            <div 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                minimizeAssistant();
              }}
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                backdropFilter: 'blur(1px)',
                WebkitBackdropFilter: 'blur(1px)',
                zIndex: 9998,
                cursor: 'pointer',
              }}
              aria-hidden="true"
            />
          )}
          
          {/* Aria Widget wrapped in Shadow DOM */}
          <ShadowContainer 
            styles={getAriaStyles()}
            style={{ 
              position: 'fixed',
              zIndex: 9999,
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            <div style={{ pointerEvents: 'auto' }}>
              <AriaWidgetStandalone
                onClose={closeAssistant}
                isMinimized={isMinimized}
                onMinimize={minimizeAssistant}
                onMaximize={maximizeAssistant}
                theme={theme}
                displayMode={displayMode}
                isConnected={isConnected}
                isLoading={isLoading}
                chatMessages={chatMessages}
                liveTranscript={liveTranscript}
                isChatVisible={isChatVisible}
                isMuted={isMuted}
                error={error}
                toolCallState={toolCallState}
                sessionState={sessionState}
                setVideoElement={setVideoElement}
                toggleChat={toggleChat}
                sendMessage={sendMessage}
                toggleMute={toggleMute}
                refreshSession={refreshSession}
              />
            </div>
          </ShadowContainer>
        </>
      )}
    </>
  );
};

// ============================================================================
// Standalone Trigger Button (doesn't use context)
// ============================================================================

interface AriaTriggerButtonStandaloneProps {
  showAvatar?: boolean;
  label?: string;
  theme: Theme;
  isReady?: boolean;
  onClick: () => void;
}

const AriaTriggerButtonStandalone: FC<AriaTriggerButtonStandaloneProps> = ({
  showAvatar = true,
  label,
  theme,
  isReady = false,
  onClick,
}) => {
  const isDark = theme === 'dark';
  const buttonLabel = label || 'Talk to Aria';
  
  return (
    <ShadowContainer 
      styles={getAriaStyles()}
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        right: '1.5rem',
        zIndex: 9997,
      }}
    >
      <div className={cn('aria-assistant-container', isDark && 'aria-dark')}>
        <button
          onClick={(e) => {
            e.preventDefault();
            if (isReady) {
              onClick();
            }
          }}
          disabled={!isReady}
          className={cn(
            "group relative flex items-center gap-3 px-3.5 py-2.5 rounded-xl",
            "transition-all duration-200 ease-out",
            "aria-animate-slide-up",
            "border-none outline-none",
            // Disabled state - more prominent visual feedback
            !isReady && "opacity-50 pointer-events-none",
            // Light mode styling
            !isDark && [
              "bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)]",
              isReady && "hover:bg-gray-50 hover:shadow-[0_8px_30px_rgb(0,0,0,0.16)] hover:-translate-y-0.5",
            ],
            // Dark mode styling
            isDark && [
              "bg-gray-900 shadow-[0_8px_30px_rgb(0,0,0,0.3)]",
              isReady && "hover:bg-gray-850 hover:shadow-[0_8px_30px_rgb(0,0,0,0.4)] hover:-translate-y-0.5",
            ],
          )}
        >
          {/* Avatar/Icon */}
          {showAvatar ? (
            <div className={cn(
              "relative w-9 h-9 rounded-lg overflow-hidden shrink-0",
              "shadow-sm transition-all duration-200",
              !isDark 
                ? "bg-gray-100" 
                : "bg-gray-800",
              // Grayscale when not ready
              !isReady && "grayscale"
            )}>
              <img 
                src={ariaAvatar} 
                alt={buttonLabel}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  // Show sparkles as fallback
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div 
                className="hidden w-full h-full items-center justify-center aria-gradient-bg text-white"
              >
                <Sparkles className="w-4 h-4" />
              </div>
              {/* Online indicator - shows session readiness */}
              <span className={cn(
                "absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2",
                !isDark ? "border-white" : "border-gray-900",
                isReady ? "bg-emerald-500" : "bg-amber-500 animate-pulse"
              )} />
            </div>
          ) : (
            <div className={cn(
              "relative w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
              "aria-gradient-bg text-white shadow-sm",
              "transition-transform duration-200",
              isReady && "group-hover:scale-105",
              !isReady && "grayscale"
            )}>
              <Sparkles className="w-4 h-4" />
            </div>
          )}
          
          {/* Text content */}
          <div className="relative flex flex-col items-start min-w-0 text-left">
            <span className={cn(
              "font-bold text-[13px] tracking-tight leading-none",
              !isDark ? "text-gray-900" : "text-white"
            )}>
              {buttonLabel}
            </span>
            <span className={cn(
              "text-[10px] font-medium mt-1 leading-none",
              !isDark ? "text-gray-500" : "text-gray-400",
              !isReady && "animate-pulse"
            )}>
              {isReady ? "I'm here to chat" : "Connecting..."}
            </span>
          </div>
        </button>
      </div>
    </ShadowContainer>
  );
};

// ============================================================================
// Standalone Widget (doesn't use context)
// ============================================================================

interface AriaWidgetStandaloneProps {
  onClose: () => void;
  isMinimized: boolean;
  onMinimize: () => void;
  onMaximize: () => void;
  theme: Theme;
  displayMode: 'center' | 'bottom-right';
  isConnected: boolean;
  isLoading: boolean;
  chatMessages: ChatMessage[];
  liveTranscript: string;
  isChatVisible: boolean;
  isMuted: boolean;
  error: string | null;
  toolCallState: ToolCallState;
  sessionState: SessionState;
  setVideoElement: (element: HTMLVideoElement | null) => void;
  toggleChat: () => void;
  sendMessage: (message: string) => Promise<void>;
  toggleMute: () => void;
  refreshSession: () => void;
}

const AriaWidgetStandalone: FC<AriaWidgetStandaloneProps> = ({
  onClose,
  isMinimized,
  onMinimize,
  onMaximize,
  theme,
  displayMode,
  isConnected,
  isLoading,
  chatMessages,
  liveTranscript,
  isChatVisible,
  isMuted,
  error,
  toolCallState,
  setVideoElement,
  toggleChat,
  sendMessage,
  toggleMute,
  refreshSession,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const transcriptRef = useRef<HTMLParagraphElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isDark = theme === 'dark';
  const isFloating = displayMode === 'bottom-right';
  const isCenter = displayMode === 'center';

  // Auto-scroll transcript to bottom when new content arrives
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [liveTranscript]);

  // Register the video element when it mounts
  useEffect(() => {
    if (videoRef.current) {
      console.log('[AriaWidgetStandalone] Registering video element, minimized:', isMinimized);
      setVideoElement(videoRef.current);
    }
    return () => {
      setVideoElement(null);
    };
  }, [setVideoElement, isMinimized]);

  // Auto-scroll chat messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !isConnected || isSending) return;

    const messageToSend = inputValue.trim();
    setInputValue('');
    setIsSending(true);

    try {
      await sendMessage(messageToSend);
    } catch (err) {
      console.error('Failed to send message:', err);
      setInputValue(messageToSend);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Minimized state: smaller compact widget
  if (isMinimized) {
    return (
      <div
        className={cn(
          'aria-assistant-container flex flex-col overflow-hidden aria-animate-scale-in',
          isDark && 'aria-dark',
          'shadow-2xl rounded-xl',
          !isDark && 'bg-white/95 border border-gray-200/80',
          isDark && 'bg-gray-950/95 border border-gray-800/50',
          'fixed bottom-6 right-6',
          'w-[320px] h-[240px]'
        )}
        style={{
          zIndex: 9999,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <div className={cn(
          'relative flex-1 flex items-center justify-center overflow-hidden',
          !isDark ? 'bg-gradient-to-br from-gray-100 to-gray-50' : 'bg-gradient-to-br from-gray-900 to-black'
        )}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />

          {/* Tool Call Indicator */}
          {toolCallState.isActive && isConnected && (
            <div className={cn(
              'absolute top-3 left-3 aria-animate-fade-in z-20',
              'flex items-center gap-2 px-3 py-2 rounded-lg',
              'shadow-lg',
              !isDark 
                ? 'bg-amber-50 border border-amber-200' 
                : 'bg-amber-900/30 border border-amber-700/50'
            )}
            style={{ backdropFilter: 'blur(8px)' }}
            >
              <div className={cn(
                'w-5 h-5 rounded-full flex items-center justify-center',
                !isDark ? 'bg-amber-100' : 'bg-amber-800/50'
              )}>
                <Wrench className={cn(
                  'w-3 h-3 animate-pulse',
                  !isDark ? 'text-amber-600' : 'text-amber-400'
                )} />
              </div>
              <span className={cn(
                'text-[10px] font-semibold',
                !isDark ? 'text-amber-800' : 'text-amber-200'
              )}>
                {toolCallState.toolName 
                  ? toolCallState.toolName.replace(/_/g, ' ')
                  : 'Processing...'
                }
              </span>
            </div>
          )}

          {/* Control Bar */}
          {isConnected && !error && (
            <div className={cn(
              'absolute bottom-3 left-1/2 transform -translate-x-1/2 z-20',
              'flex items-center gap-1 p-1 rounded-full',
              'shadow-xl',
              !isDark 
                ? 'bg-white/90' 
                : 'bg-gray-900/90'
            )}
            style={{ backdropFilter: 'blur(12px)' }}
            >
              <button
                onClick={toggleMute}
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200',
                  'border-0 outline-none focus:outline-none shadow-sm',
                  isMuted 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : !isDark
                      ? 'bg-gray-100/80 hover:bg-gray-200 text-gray-600'
                      : 'bg-gray-800/80 hover:bg-gray-700 text-gray-300'
                )}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              </button>

              <button
                onClick={onMaximize}
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200',
                  'border-0 outline-none focus:outline-none shadow-sm',
                  !isDark
                    ? 'bg-indigo-500 hover:bg-indigo-600 text-white'
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                )}
                title="Maximize"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
              
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all duration-200 text-white border-0 outline-none focus:outline-none shadow-sm"
                title="End Call"
              >
                <Phone className="h-3.5 w-3.5 rotate-[135deg]" />
              </button>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className={cn(
              'absolute inset-0 flex flex-col items-center justify-center z-10',
              !isDark ? 'bg-gradient-to-br from-gray-50 to-white' : 'bg-gradient-to-br from-gray-900 to-gray-950'
            )}>
              <div className="relative mb-3">
                <div className={cn(
                  'absolute -inset-2 rounded-full animate-spin',
                  !isDark 
                    ? 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500' 
                    : 'bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400'
                )} style={{ animationDuration: '3s' }}>
                  <div className={cn(
                    'absolute inset-[2px] rounded-full',
                    !isDark ? 'bg-white' : 'bg-gray-900'
                  )} />
                </div>
                <div className={cn(
                  'relative w-12 h-12 rounded-full overflow-hidden shadow-lg',
                  !isDark ? 'bg-gray-100 ring-2 ring-white' : 'bg-gray-800 ring-2 ring-gray-900'
                )}>
                  <img 
                    src={ariaAvatar} 
                    alt="Aria" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              </div>
              <p className={cn(
                'text-xs font-medium',
                !isDark ? 'text-gray-600' : 'text-gray-400'
              )}>
                Connecting...
              </p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className={cn(
              'absolute inset-0 flex flex-col items-center justify-center z-10 p-4',
              !isDark ? 'bg-white/90' : 'bg-gray-950/90'
            )}>
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center mb-2">
                <X className="w-5 h-5 text-red-500" />
              </div>
              <p className={cn(
                'text-xs font-medium text-center mb-2',
                !isDark ? 'text-red-700' : 'text-red-300'
              )}>
                Connection error
              </p>
              <button
                onClick={refreshSession}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium',
                  'transition-all duration-200',
                  !isDark 
                    ? 'bg-indigo-500 hover:bg-indigo-600 text-white' 
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                )}
              >
                <RefreshCw className="w-3 h-3" />
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full size widget
  return (
    <div
      className={cn(
        'aria-assistant-container flex flex-col overflow-hidden aria-animate-scale-in',
        isDark && 'aria-dark',
        'shadow-2xl',
        !isDark && 'bg-white/95 border border-gray-200/80',
        isDark && 'bg-gray-950/95 border border-gray-800/50',
        isFloating && 'fixed bottom-6 right-6 rounded-xl',
        isFloating && (isChatVisible ? 'w-[960px]' : 'w-[640px]'),
        isFloating && 'h-[500px]',
        isCenter && 'fixed inset-8 md:inset-16 lg:inset-20 rounded-2xl'
      )}
      style={{
        zIndex: 9999,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Video Section */}
        <div className={cn(
          'relative flex items-center justify-center overflow-hidden',
          !isDark ? 'bg-gradient-to-br from-gray-100 to-gray-50' : 'bg-gradient-to-br from-gray-900 to-black',
          isChatVisible ? 'flex-1' : 'w-full'
        )}>
          {/* Loading State */}
          {isLoading && (
            <div className={cn(
              'absolute inset-0 flex flex-col items-center justify-center z-10',
              !isDark ? 'bg-gradient-to-br from-gray-50 to-white' : 'bg-gradient-to-br from-gray-900 to-gray-950'
            )}>
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className={cn(
                  'absolute w-96 h-96 rounded-full opacity-20 blur-3xl',
                  'animate-pulse',
                  !isDark ? 'bg-indigo-300' : 'bg-indigo-900'
                )} style={{ top: '20%', left: '10%', animationDuration: '3s' }} />
                <div className={cn(
                  'absolute w-64 h-64 rounded-full opacity-20 blur-3xl',
                  'animate-pulse',
                  !isDark ? 'bg-purple-300' : 'bg-purple-900'
                )} style={{ bottom: '20%', right: '10%', animationDuration: '4s', animationDelay: '1s' }} />
              </div>
              
              <div className="relative flex flex-col items-center">
                <div className="relative mb-6">
                  <div className={cn(
                    'absolute -inset-3 rounded-full',
                    'animate-spin',
                    !isDark 
                      ? 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500' 
                      : 'bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400'
                  )} style={{ animationDuration: '3s' }}>
                    <div className={cn(
                      'absolute inset-[3px] rounded-full',
                      !isDark ? 'bg-white' : 'bg-gray-900'
                    )} />
                  </div>
                  
                  <div className={cn(
                    'absolute -inset-1.5 rounded-full opacity-60 animate-pulse',
                    !isDark 
                      ? 'bg-gradient-to-r from-indigo-400 to-purple-400' 
                      : 'bg-gradient-to-r from-indigo-500 to-purple-500'
                  )} />
                  
                  <div className={cn(
                    'relative w-20 h-20 rounded-full overflow-hidden',
                    'shadow-xl',
                    !isDark ? 'bg-gray-100 ring-4 ring-white' : 'bg-gray-800 ring-4 ring-gray-900'
                  )}>
                    <img 
                      src={ariaAvatar} 
                      alt="Aria" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
                
                <h3 className={cn(
                  'text-2xl font-bold tracking-tight mb-2',
                  !isDark ? 'text-gray-900' : 'text-white'
                )}>
                  Aria is joining...
                </h3>
                
                <div className="flex items-center gap-1.5 mb-6">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={cn(
                        'w-2.5 h-2.5 rounded-full',
                        !isDark ? 'bg-indigo-500' : 'bg-indigo-400'
                      )}
                      style={{
                        animation: 'aria-loading-bounce 1.4s ease-in-out infinite',
                        animationDelay: `${i * 0.15}s`,
                      }}
                    />
                  ))}
                </div>
                
                <p className={cn(
                  'text-sm font-medium px-4 py-2 rounded-full',
                  !isDark 
                    ? 'bg-gray-100 text-gray-600' 
                    : 'bg-gray-800 text-gray-400'
                )}>
                  Get ready to chat
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className={cn(
              'absolute inset-0 flex flex-col items-center justify-center z-10 p-6',
              !isDark ? 'bg-white/80' : 'bg-gray-950/80'
            )}
            style={{ backdropFilter: 'blur(4px)' }}
            >
              <div className={cn(
                'rounded-lg p-6 max-w-sm w-full shadow-lg border',
                !isDark ? 'bg-white border-red-100' : 'bg-gray-900 border-red-900/30'
              )}>
                <div className="flex flex-col items-center text-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                    <X className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <p className={cn(
                      'text-base font-bold mb-2',
                      !isDark ? 'text-red-900' : 'text-red-200'
                    )}>
                      Couldn't reach Aria
                    </p>
                    <p className={cn(
                      'text-sm leading-relaxed mb-4',
                      !isDark ? 'text-red-600' : 'text-red-400'
                    )}>
                      {error}
                    </p>
                    <button
                      onClick={refreshSession}
                      className={cn(
                        'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
                        'transition-all duration-200',
                        !isDark 
                          ? 'bg-indigo-500 hover:bg-indigo-600 text-white' 
                          : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                      )}
                    >
                      <RefreshCw className="w-4 h-4" />
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Video Element */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full h-full object-contain"
          />

          {/* Tool Call Indicator */}
          {toolCallState.isActive && isConnected && (
            <div className={cn(
              'absolute top-3 left-3 aria-animate-fade-in z-20',
              'flex items-center gap-2 px-3 py-2 rounded-lg',
              'shadow-lg',
              !isDark 
                ? 'bg-amber-50 border border-amber-200' 
                : 'bg-amber-900/30 border border-amber-700/50'
            )}
            style={{ backdropFilter: 'blur(8px)' }}
            >
              <div className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center',
                !isDark ? 'bg-amber-100' : 'bg-amber-800/50'
              )}>
                <Wrench className={cn(
                  'w-3.5 h-3.5 animate-pulse',
                  !isDark ? 'text-amber-600' : 'text-amber-400'
                )} />
              </div>
              <div className="flex flex-col">
                <span className={cn(
                  'text-xs font-semibold',
                  !isDark ? 'text-amber-800' : 'text-amber-200'
                )}>
                  {toolCallState.toolName 
                    ? `Running: ${toolCallState.toolName.replace(/_/g, ' ')}`
                    : 'Processing...'
                  }
                </span>
                <span className={cn(
                  'text-[10px]',
                  !isDark ? 'text-amber-600' : 'text-amber-400'
                )}>
                  Please wait...
                </span>
              </div>
              <div className="flex items-center gap-0.5 ml-1">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={cn(
                      'w-1.5 h-1.5 rounded-full',
                      !isDark ? 'bg-amber-500' : 'bg-amber-400'
                    )}
                    style={{
                      animation: 'aria-loading-bounce 1.4s ease-in-out infinite',
                      animationDelay: `${i * 0.15}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Live Transcript Overlay */}
          {liveTranscript && isConnected && !toolCallState.isActive && (
            <div className={cn(
              'absolute top-0 left-0 right-0 aria-animate-fade-in',
              'px-4 py-2',
              !isDark 
                ? 'bg-gradient-to-b from-black/60 to-transparent text-white' 
                : 'bg-gradient-to-b from-white/60 to-transparent text-gray-900'
            )}
            style={{ backdropFilter: 'blur(4px)' }}
            >
              <div className="flex items-start gap-2 max-w-full">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0 mt-1" />
                <p ref={transcriptRef} className="text-xs leading-relaxed flex-1 max-h-[3.5em] overflow-y-auto scrollbar-hide">
                  {liveTranscript}
                </p>
              </div>
            </div>
          )}

          {/* Control Bar */}
          {isConnected && !error && (
            <div className={cn(
              'absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20',
              'flex items-center gap-1.5 p-1 rounded-full',
              'shadow-xl',
              !isDark 
                ? 'bg-white/90' 
                : 'bg-gray-900/90'
            )}
            style={{ backdropFilter: 'blur(12px)' }}
            >
              <button
                onClick={toggleChat}
                className={cn(
                  'w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200',
                  'border-0 outline-none focus:outline-none shadow-sm',
                  isChatVisible
                    ? !isDark
                      ? 'bg-indigo-500 hover:bg-indigo-600 text-white'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : !isDark
                      ? 'bg-gray-100/80 hover:bg-gray-200 text-gray-600'
                      : 'bg-gray-800/80 hover:bg-gray-700 text-gray-300'
                )}
                title={isChatVisible ? 'Hide Chat' : 'Show Chat'}
              >
                <MessageCircle className="h-4 w-4" />
              </button>
              
              <button
                onClick={toggleMute}
                className={cn(
                  'w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200',
                  'border-0 outline-none focus:outline-none shadow-sm',
                  isMuted 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : !isDark
                      ? 'bg-gray-100/80 hover:bg-gray-200 text-gray-600'
                      : 'bg-gray-800/80 hover:bg-gray-700 text-gray-300'
                )}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>

              <button
                onClick={onMinimize}
                className={cn(
                  'w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200',
                  'border-0 outline-none focus:outline-none shadow-sm',
                  !isDark
                    ? 'bg-gray-100/80 hover:bg-gray-200 text-gray-600'
                    : 'bg-gray-800/80 hover:bg-gray-700 text-gray-300'
                )}
                title="Minimize"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              
              <button
                onClick={onClose}
                className="w-10 h-9 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all duration-200 text-white border-0 outline-none focus:outline-none shadow-sm"
                title="End Call"
              >
                <Phone className="h-4 w-4 rotate-[135deg]" />
              </button>
            </div>
          )}
        </div>

        {/* Chat Panel - Inline implementation */}
        {isChatVisible && (
          <div className={cn(
            'flex flex-col shrink-0 border-l',
            isFloating ? 'w-[320px]' : 'w-[360px]',
            !isDark ? 'bg-white border-gray-100' : 'bg-gray-900 border-gray-800'
          )}>
            {/* Chat Header */}
            <div className={cn(
              'px-4 py-3 border-b',
              !isDark ? 'border-gray-100 bg-white' : 'border-gray-800 bg-gray-900'
            )}>
              <div className="flex items-center gap-2">
                <div className={cn(
                  'w-7 h-7 rounded-lg flex items-center justify-center',
                  !isDark ? 'bg-indigo-100' : 'bg-indigo-900/30'
                )}>
                  <MessageCircle className={cn(
                    'w-4 h-4',
                    !isDark ? 'text-indigo-600' : 'text-indigo-400'
                  )} />
                </div>
                <h3 className={cn(
                  'font-bold text-sm',
                  !isDark ? 'text-gray-900' : 'text-white'
                )}>
                  Chat with Aria
                </h3>
              </div>
            </div>

            {/* Messages Container */}
            <div className={cn(
              'flex-1 overflow-y-auto p-4 space-y-4',
              !isDark ? 'bg-gray-50/30' : 'bg-gray-900/30'
            )}>
              {chatMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4 aria-animate-fade-in">
                  <div className={cn(
                    'rounded-2xl p-6 mb-4',
                    !isDark 
                      ? 'bg-gradient-to-br from-indigo-50 to-purple-50' 
                      : 'bg-gradient-to-br from-indigo-900/20 to-purple-900/20'
                  )}>
                    <Sparkles className={cn(
                      'h-8 w-8',
                      !isDark ? 'text-indigo-500' : 'text-indigo-400'
                    )} />
                  </div>
                  <p className={cn(
                    'text-sm font-bold mb-1',
                    !isDark ? 'text-gray-900' : 'text-white'
                  )}>
                    Let's talk!
                  </p>
                  <p className={cn(
                    'text-xs',
                    !isDark ? 'text-gray-500' : 'text-gray-400'
                  )}>
                    Aria is here to chat with you
                  </p>
                </div>
              ) : (
                chatMessages
                  .filter((message) => message.content && message.content.trim() !== '')
                  .map((message, index) => (
                  <div
                    key={index}
                    className={cn(
                      'flex gap-2 aria-animate-fade-in',
                      message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                    )}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div
                      className={cn(
                        'shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
                        message.role === 'user'
                          ? 'bg-indigo-500 text-white'
                          : !isDark
                            ? 'bg-gray-200 text-gray-600'
                            : 'bg-gray-700 text-gray-300'
                      )}
                    >
                      {message.role === 'user' ? (
                        <User className="h-3 w-3" />
                      ) : (
                        <Bot className="h-3 w-3" />
                      )}
                    </div>

                    <div
                      className={cn(
                        'flex flex-col max-w-[80%]',
                        message.role === 'user' ? 'items-end' : 'items-start'
                      )}
                    >
                      <div
                        className={cn(
                          'px-3 py-2 rounded-2xl',
                          message.role === 'user'
                            ? 'bg-indigo-500 text-white rounded-br-md'
                            : !isDark
                              ? 'bg-white shadow-sm text-gray-800 rounded-bl-md'
                              : 'bg-gray-800 text-gray-100 rounded-bl-md'
                        )}
                      >
                        <p className={cn(
                          'text-xs leading-relaxed break-words',
                          message.role === 'user' && 'text-white'
                        )}>
                          {message.content}
                        </p>
                      </div>
                      <span className={cn(
                        'text-[9px] mt-1 px-1',
                        !isDark ? 'text-gray-400' : 'text-gray-500'
                      )}>
                        {formatTimestamp(message.timestamp)}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className={cn(
              'p-3 border-t',
              !isDark ? 'border-gray-100 bg-white' : 'border-gray-800 bg-gray-900'
            )}>
              <div className={cn(
                'flex gap-2 p-1 rounded-lg border transition-all duration-200',
                !isDark 
                  ? 'bg-gray-50 border-gray-200 focus-within:border-indigo-500/50 focus-within:bg-white' 
                  : 'bg-gray-850 border-gray-700 focus-within:border-indigo-500/50'
              )}>
                <input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={isConnected ? 'Message Aria...' : 'Connecting...'}
                  disabled={!isConnected || isSending}
                  className={cn(
                    'flex-1 border-0 bg-transparent shadow-none focus:outline-none px-2 h-8 text-xs',
                    !isDark ? 'placeholder:text-gray-400 text-gray-900' : 'placeholder:text-gray-500 text-white'
                  )}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!isConnected || !inputValue.trim() || isSending}
                  className={cn(
                    'h-7 w-7 rounded-md shrink-0 transition-all duration-200 flex items-center justify-center',
                    'bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white'
                  )}
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AriaStandaloneUI;
